"use client";

import Image from "next/image";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CustomButton from "./CustomButton";
import CallExpandBtn from "./CallExpandBtn";
import AnimateOnScroll from "./AnimateOnScroll";
import LocationsMap from "./LocationsMap";
import { ThemeName, THEME_HEX } from "./theme";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import { ROUTES } from "@/lib/routes";
import {
  trackBookAppointmentClicked,
  trackContactFormSubmitted,
} from "@/lib/analytics";
import { ExternalLink } from "lucide-react";

// Service types for deep linking to booking flow
type BookingServiceType =
  | "counselling"
  | "life-coach"
  | "occupational-therapy"
  | "psychology";

const COUNSELLING_PATHS = [
  "/counselling",
  "/anger-management",
  "/relationships",
  "/depression-anxiety",
  "/grief-loss",
  "/career-counselling",
  "/life-coach",
  "/self-esteem-confidence",
  "/time-management",
  "/focus-productivity",
  "/changing-habits",
  "/public-speaking",
];

const ALLIED_HEALTH_PATHS = [
  "/allied-health",
  "/occupational-therapy",
  "/psychology",
];

const matchesPath = (paths: string[], pathname: string) =>
  paths.some((p) => pathname === p || pathname.startsWith(p + "/"));

const hours = [
  { day: "Monday", time: "9:00 AM – 8:00 PM" },
  { day: "Tuesday", time: "9:00 AM – 8:00 PM" },
  { day: "Wednesday", time: "9:00 AM – 8:00 PM" },
  { day: "Thursday", time: "9:00 AM – 8:00 PM" },
  { day: "Friday", time: "9:00 AM – 8:00 PM" },
  { day: "Saturday", time: "10:00 AM – 6:00 PM" },
  { day: "Sunday", time: "10:00 AM – 6:00 PM" },
];

const fieldBase =
  "3xl:h-[50px] h-[40px] w-full rounded-none border-t-0 border-x-0 border-b border-black/30 bg-transparent px-0 font-onest text-[20px] font-light placeholder:text-black/40 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-b-black focus-visible:border-b-black focus-visible:outline-none transition-colors shadow-none";

interface ContactProps {
  theme?: Exclude<ThemeName, "white">;
  /** Optional service type to pre-select in booking flow */
  bookingService?: BookingServiceType;
  showMap?: boolean;
}

const Contact = ({
  theme = "blue",
  bookingService,
  showMap = true,
}: ContactProps) => {
  const themeColor = THEME_HEX[theme];
  const bookingUrl = bookingService
    ? `${ROUTES.booking}?service=${bookingService}`
    : ROUTES.booking;
  const pathname = usePathname();
  const isCounsellingPage = matchesPath(COUNSELLING_PATHS, pathname);
  const isAlliedHealthPage = matchesPath(ALLIED_HEALTH_PATHS, pathname);
  const showCounselling = !isAlliedHealthPage;
  const showAlliedHealth = !isCounsellingPage;
  const showLabels = showCounselling && showAlliedHealth;

  const openingHoursSpecification = [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
  ];

  const locations = [
    showCounselling && {
      "@type": "LocalBusiness",
      name: `${ORGANIZATION_NAME} - Counselling, Life Coaching & Allied Health`,
      telephone: "+61429220646",
      url: SITE_URL,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Suite 207a, 30 Campbell St",
        addressLocality: "Blacktown",
        addressRegion: "NSW",
        postalCode: "2148",
        addressCountry: "AU",
      },
      hasMap: "https://maps.app.goo.gl/aJ1h9VTjYA5fxavb8",
      openingHoursSpecification,
    },
    showAlliedHealth && {
      "@type": "LocalBusiness",
      name: `${ORGANIZATION_NAME} - Allied Health`,
      telephone: "+61429220646",
      url: SITE_URL,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Suite 15.01, Level 15, 60 Station St East",
        addressLocality: "Parramatta",
        addressRegion: "NSW",
        postalCode: "2150",
        addressCountry: "AU",
      },
      hasMap: "https://maps.app.goo.gl/9Z4jpyaDuSZKzyX6A",
      openingHoursSpecification,
    },
  ].filter(Boolean);

  const localBusinessSchema =
    locations.length > 1
      ? { "@context": "https://schema.org", "@graph": locations }
      : { "@context": "https://schema.org", ...locations[0] };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Toast first error
      const firstError = Object.values(validationErrors)[0];
      toast.error(firstError);
      return;
    }

    setLoading(true);
    try {
      const baseUrl =
        process.env.NEXT_PUBLIC_BASEURL ||
        "https://alex.api.strategybyte.com.au/api/v1";
      const response = await fetch(`${baseUrl}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        trackContactFormSubmitted({ location: pathname });
        toast.success(
          "Inquiry submitted successfully! Confirmation email sent.",
        );
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error(
          data.message || "Failed to submit inquiry. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-[#E1EEFF] to-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema).replace(/</g, "\\u003c"),
        }}
      />
      {/* ══════════════ CONTACT FORM SECTION ══════════════ */}
      <section className="container" aria-labelledby="contact-heading">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14 3xl:gap-16">
          {/* Left: form */}
          <div className="col-span-1 flex flex-col gap-8">
            {/* Header */}
            <AnimateOnScroll className="flex flex-col gap-2">
              <span
                data-from-left
                className={`font-crimsonText text-[20px] font-bold lg:text-[28px]`}
              >
                Contact Us
              </span>
              <h2
                id="contact-heading"
                className={`heading ${theme === "blue" ? "text-v3-secondary" : "text-black"}`}
              >
                Take the First Step
                <br />
                Toward Support
              </h2>
              <p className="font-onest text-[18px] font-normal leading-[125%] text-black/70">
                If you are looking for guidance or a place to talk, we are here
                to help. Reach out to start a conversation in a safe and private
                environment. We are committed to providing caring, professional
                support tailored to your unique needs.
              </p>
            </AnimateOnScroll>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col lg:gap-2 3xl:gap-6"
              noValidate
              aria-label="Contact form"
            >
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name*"
                className={`${fieldBase} ${errors.name ? "border-b-red-500 focus:border-b-red-500" : ""}`}
                aria-label="Full name"
                required
              />
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address*"
                className={`${fieldBase} ${errors.email ? "border-b-red-500 focus:border-b-red-500" : ""}`}
                aria-label="Email address"
                required
              />
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number*"
                className={`${fieldBase} ${errors.phone ? "border-b-red-500 focus:border-b-red-500" : ""}`}
                aria-label="Phone number"
                required
              />
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Write your message......"
                className={`min-h-[60px] w-full resize-none rounded-none border-x-0 border-b border-t-0 border-black/30 bg-transparent px-0 pb-3 pt-6 font-onest text-[20px] font-light shadow-none transition-colors placeholder:text-black/40 focus:border-b-black focus-visible:border-b-black focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 3xl:min-h-[120px] ${errors.message ? "border-b-red-500 focus:border-b-red-500" : ""}`}
                rows={3}
                aria-label="Message"
                required
              />

              <div className="mt-2">
                <CustomButton
                  label={loading ? "Sending..." : "Get in Touch"}
                  variant="primary"
                  withIcon
                  size="lg"
                  theme={theme}
                />
              </div>
            </form>
          </div>

          {/* Right: image */}
          <div className="relative col-span-1 h-[550px] overflow-hidden 3xl:h-[600px]">
            <Image
              src="/images/v3/home/contact-side-img.png"
              alt="Counselling practice"
              fill
              className="h-full w-auto"
              sizes=""
            />
          </div>
        </div>
      </section>

      {/* ══════════════ MAP SECTION ══════════════ */}
      {showMap && (
        <LocationsMap theme={theme} bookingService={bookingService} />
      )}
    </div>
  );
};

export default Contact;
